/**
 * Helper functions as properties of the
 * helpers object.
 */

/**
* Throttle function, that fires a message
* every 250ms by default (rather than at
* the end of a burst of events).
*/
export function throttle( fn, threshhold, scope ) {
  if ( threshhold === undefined ) {
    threshhold = 250
  }
  var last,
    deferTimer
  return function() {
    var context = scope || this

    var now = +new Date()
    var args = arguments
    if ( last && now < last + threshhold ) {
      // hold on to it
      clearTimeout( deferTimer )
      deferTimer = setTimeout( function() {
        last = now
        fn.apply( context, args )
      }, threshhold )
    } else {
      last = now
      fn.apply( context, args )
    }
  }
}

/**
 * Returns a function, that, as long as it continues to be invoked,
 * will not be triggered. The function will be called after it stops
 * being called for N milliseconds. If `immediate` is passed,
 * trigger the function on the leading edge, instead of the trailing.
 *
 * @param {function} func
 * @param {integer} wait
 * @param {boolean} immediate
 */
export function debounce( func, wait, immediate ) {
  var timeout
  return function() {
    var context = this, args = arguments
    // eslint-disable-next-line require-jsdoc
    var later = function() {
      timeout = null
      if ( !immediate ) func.apply( context, args )
    }
    var callNow = immediate && !timeout
    clearTimeout( timeout )
    timeout = setTimeout( later, wait )
    if ( callNow ) func.apply( context, args )
  }
}

/**
 * Function checks if the given element is fully visible
 * in the viewport.
 *
 * @param {DOMNode} element
 * @return {boolean} True if the element is fully visible, otherwise false.
 */
export function isElementFullyVisibleInViewport( element ) {
  var rect = element.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= ( window.innerHeight || document.documentElement.clientHeight ) &&
    rect.right <= ( window.innerWidth || document.documentElement.clientWidth )
  )
}

/**
 * Function checks if the given element is partially visible
 * in the viewport.
 *
 * @param {DOMNode} element
 * @return {boolean} True if the element is partially visible, otherwise false.
 */
export function isElementPartiallyVisibleInViewport( element ) {
  var rect = element.getBoundingClientRect()
  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.left < ( window.innerWidth || document.documentElement.clientWidth ) &&
    rect.top < ( window.innerHeight || document.documentElement.clientHeight )
  )
}

/**
 * Function is used to return the value of given html <meta> field.
 *
 * @param {string} name
 * @return {string} Value of the meta field.
 */
export function getMetaValue( name ) {
  var metas = document.getElementsByTagName( 'meta' )

  for ( var i = 0; i < metas.length; i++ ) {
    if ( metas[i].getAttribute( 'name' ) === name ) {
      return metas[i].getAttribute( 'content' )
    }
  }

  return ''
}

/**
 * Sleep function.
 *
 * @param {int} ms
 */
export const sleep = ( ms ) => {
  return new Promise(( resolve ) => setTimeout( resolve, ms ))
}

/**
 *
 * @param {Response} response
 */
export async function checkStatus( response ) {
  if ( response.status >= 200 && response.status < 300 ) {
    return response
  } else {
    var error = new Error( response.statusText )
    error.response = await response.json()
    throw error
  }
}

/**
 *
 * @param {Response} response
 * @return {object}
 */
export function parseJSON( response ) {
  return response.json()
}

/**
 *
 * @param {object} translations
 * @param {string} translate
 * @return {string}
 */
export function translate( translations, translate ) {
  if ( Object.prototype.hasOwnProperty.call( translations, translate )) {
    return translations[translate]
  }
  return translate
}

/**
 *
 * @param {object} translations
 * @param {string} translate
 * @param {int} count
 * @return {string}
 */
export function translateN( translations, translate, count ) {
  if ( Object.prototype.hasOwnProperty.call( translations, translate )) {
    const values = translations[translate]
    return values[count === 1 ? 0 : 1].replace( '%d', count )
  }
  return translate.replace( '%d', count )
}

/**
 * Collect styles generated by styled components
 * and add those to our custom style tag for
 * SSR purposes.
 *
 * @link https://spectrum.chat/styled-components/help/get-css-styles-working-with-prerender-io~4483f08d-7c1f-4c51-b0c9-b3430b8b0212?m=MTU0ODYyNjY1MDM4Mg==
 */
export function addStyledComponentStyles() {
  const searchAppRoot = document.querySelector( '#search-app' )
  if ( searchAppRoot ) {
    const el = document.createElement( 'style' )
    el.setAttribute( 'data-sponda-ssr', true )
    setTimeout(() => {
      const styles = document.querySelectorAll( 'style[data-styled]' )
      for ( let style of styles.values()) {
        for ( let rule of style.sheet.rules ) {
          el.appendChild( document.createTextNode( rule.cssText ))
        }
      }
      searchAppRoot.prepend( el )
    }, 1000 )
  }
}
